import React, {useEffect, useRef, useState} from "react";
import CanvasDraw from "react-canvas-draw";
import {useAIToolsContext} from "../../../../../../../../../../context/AIToolsProvider";
import useFetchAuthImage from "../../../../../../../../../../../../../../Hooks/useFetchAuthImage";
import styles from './HighlightAndSwap.module.scss'
import Trash from "../../../../../../../../../../../../../../Assets/Icons/trash-purple.svg";
import Document from "../../../../../../../../../../../../../../Assets/Icons/document-upload.svg";
import {useADSContext} from "../../../../../../../../../../context/ADSProvider";
// container can be any height,
// but must not exceed MaxContainerWidth
const MinContainerHeight = 528
const MaxContainerWidth = 528
const ScaleFactor = 1.5

const HighlightAndSwap = ({imagePath}) => {
  const {canvasRef, brushSize, setMaskImage, setCanvasHasChanges, firstSelectedImage, setFirstSelectedImage} = useAIToolsContext();
  const {selectedProduct, handleSelectProduct} = useADSContext();
  const {data: imageSrc, loading: loadingImageSrc} = useFetchAuthImage({path: imagePath});

  const [uploadedImgUrl, setUploadedImgUrl] = useState(null);

  const [imageNaturalDimensions, setImageNaturalDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  const hiddenCanvasRef = useRef(null);
  const containerRef = useRef(null);

  const [inputKey, setInputKey] = useState(Date.now());
  const inputRef = useRef(null);

  const renderImgUrl = uploadedImgUrl || imageSrc

  useEffect(() => {
    setInputKey(Date.now()); // Reset input key to force re-render
    if (firstSelectedImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImgUrl(e.target.result);
      };
      reader.readAsDataURL(firstSelectedImage);
    } else {
      setUploadedImgUrl(null);
    }
  },[firstSelectedImage])

  useEffect(() => {
    if (renderImgUrl) {
      const img = new Image();
      img.src = renderImgUrl;

      img.onload = () => {
        setImageNaturalDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
    }
  }, [renderImgUrl])

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current && imageNaturalDimensions.width && imageNaturalDimensions.height) {
        const aspectRatio = imageNaturalDimensions.width / imageNaturalDimensions.height;

        // Calculate dimensions with scale factor
        const scaledMaxWidth = MaxContainerWidth / ScaleFactor;

        // Set width to the scaled max width
        let newWidth = scaledMaxWidth;
        let newHeight = newWidth / aspectRatio;

        // Apply scale factor to get final dimensions
        newWidth *= ScaleFactor;
        newHeight *= ScaleFactor;

        setImageDimensions({
          width: newWidth,
          height: newHeight,
        });
      }
    };

    window.addEventListener("resize", updateDimensions);
    updateDimensions();
    return () => window.removeEventListener("resize", updateDimensions);
  }, [renderImgUrl, imageNaturalDimensions]);

  const createMaskImage = async () => {
    if (canvasRef.current && hiddenCanvasRef.current) {
      const drawingCanvas = canvasRef.current?.canvas?.drawing;
      const hiddenCanvas = hiddenCanvasRef?.current;
      const ctx = hiddenCanvas.getContext("2d");

      if (!drawingCanvas) {
        console.error("Drawing canvas is not available.");
        return null;
      }

      hiddenCanvas.width = imageNaturalDimensions.width;
      hiddenCanvas.height = imageNaturalDimensions.height;
      ctx.clearRect(0, 0, hiddenCanvas.width, hiddenCanvas.height);
      ctx.drawImage(
        drawingCanvas,
        0,
        0,
        drawingCanvas.width,
        drawingCanvas.height,
        0,
        0,
        hiddenCanvas.width,
        hiddenCanvas.height
      );

      return hiddenCanvas.toDataURL("image/png");
    }
    return null;
  };

  const handleChange = async () => {
    checkForChange();
    await handleMaskImage();
  };

  const handleMaskImage = async () => {
    const maskImage = await createMaskImage();
    if (maskImage) {
      setMaskImage(maskImage);
    }
  }

  const checkForChange = () => {
    try {
      setCanvasHasChanges(
        !!JSON.parse(canvasRef?.current?.getSaveData())?.lines?.length
      );
    } catch (error) {
      console.error('fail check for change: ', error);
    }
  };

  const handleDelete = () => {
    // need to consider if current product is selected
    setFirstSelectedImage(null);

    if (selectedProduct?.id) {
      handleSelectProduct(selectedProduct)
    }
  }

  const handleClickUpload = () => {
    inputRef.current?.click()
  }

  const handleUploadedFile = (event) => {
    const file = event.target.files[0];
    setFirstSelectedImage(file)

    if (selectedProduct?.id) {
      handleSelectProduct(selectedProduct)
    }
  }

  useEffect(() => {
    canvasRef.current?.clear();
  }, [canvasRef, renderImgUrl]);

  return (
    <div className={styles.wrapper}>
      <input
        key={inputKey}
        ref={inputRef}
        accept="image/*"
        style={{display: "none"}}
        id='hlns-raised-button-file'
        multiple={false}
        type="file"
        onChange={handleUploadedFile}
      />

      {renderImgUrl && (
        <>
          <div
            ref={containerRef}
            className={styles.container}
            style={{width: MaxContainerWidth, minHeight: MinContainerHeight}}
          >
            {imageDimensions.width > 0 && imageDimensions.height > 0 && (
              <CanvasDraw
                ref={canvasRef}
                canvasWidth={imageDimensions.width}
                canvasHeight={imageDimensions.height}
                imgSrc={renderImgUrl}
                brushColor="#dde2eb78"
                brushRadius={brushSize} // Use the selected brush size
                lazyRadius={0}
                onChange={handleChange}
              />
            )}
          </div>

          <section className={styles.actions}>
            <img
              alt=""
              src={Trash}
              onClick={handleDelete}
            />
            <img
              alt=""
              src={Document}
              onClick={handleClickUpload}
            />
          </section>
        </>
      )}
      <canvas ref={hiddenCanvasRef} style={{display: "none"}}/>
    </div>
  );
}

export default HighlightAndSwap
